import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const NavigationGuardContext = createContext(null);

export function NavigationGuardProvider({ children }) {
  const [isDirty, setDirty] = useState(false);
  const [message, setMessage] = useState('You have unsaved changes. Discard them and leave this page?');

  const value = useMemo(() => ({ isDirty, setDirty, message, setMessage }), [isDirty, message]);

  return (
    <NavigationGuardContext.Provider value={value}>{children}</NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
  return ctx;
}

export function GlobalNavigationGuard() {
  const { isDirty, message } = useNavigationGuard();
  const [open, setOpen] = useState(false);
  const [pendingHref, setPendingHref] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return; // nothing to guard
      if (e.defaultPrevented) return;
      if (e.button !== 0) return; // left click only
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // allow new tab, etc.
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName && el.tagName.toLowerCase() === 'a') break;
        el = el.parentElement;
      }
      if (!el || el.tagName?.toLowerCase() !== 'a') return;
      const href = el.getAttribute('href');
      const target = el.getAttribute('target');
      const rel = el.getAttribute('rel') || '';
      if (!href || href.startsWith('#')) return;
      if (target === '_blank' || rel.includes('external') || el.hasAttribute('download')) return;
      // Same-origin in-app navigation only
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;
      e.preventDefault();
      setPendingHref(url.pathname + url.search + url.hash);
      setOpen(true);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isDirty]);

  // Warn on browser/tab close or reload if there are unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const handleCancel = () => {
    setOpen(false);
    setPendingHref(null);
  };
  const handleLeave = () => {
    const to = pendingHref || '/';
    setOpen(false);
    setPendingHref(null);
    navigate(to);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Unsaved changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Stay</Button>
        <Button variant="contained" color="warning" onClick={handleLeave}>Discard & Leave</Button>
      </DialogActions>
    </Dialog>
  );
}
